import type { ITopic } from '@contexts/header/IUseHeader';
import { EMajorAreaThemeOrder } from '@enums/MajorAreaThemeOrder';
import HttpClientError from '@errors/HttpClientError';
import formatterCalculatorHtml from '@formatters/calculatorHtml';
import formatterCalculatorHtml2 from '@formatters/calculatorHtml2';
import formatterDrugHtml from '@formatters/drugHtml';
import formatterDrugHtml2 from '@formatters/drugHtml2';
import formatterExternalLinkHtml from '@formatters/externalLinkHtml';
import formatterThemeHtml from '@formatters/themeHtml';
import formatterThemeHtml2 from '@formatters/themeHtml2';
import { type MajorArea } from '@models/MajorArea';
import { type MajorAreaTheme } from '@models/MajorAreaTheme';
import HttpClient from '@services/utils/HttpClient';

import { type IGetAllMajorAreasOutputData } from './dtos/getAllMajorAreas/OutputData';
import { type IGetAllMajorAreaThemesInputData } from './dtos/getAllMajorAreaThemes/InputData';
import { type IGetAllMajorAreaThemesOutputData } from './dtos/getAllMajorAreaThemes/OutputData';
import { type IGetThemeContentInputData } from './dtos/getThemeContent/InputData';
import { type IGetThemeContentOutputData } from './dtos/getThemeContent/OutputData';
import { type IMajorAreasRepository } from './IMajorAreasRepository';

interface IGetAllResponse {
  lista: {
    id: number;
    nome: string;
  }[];
}

interface IGetThemesParams {
  especialidade?: number;
  id: number;
  ordenacao?: EMajorAreaThemeOrder;
}
interface IGetThemesResponse {
  lista: {
    e_favorito: boolean;
    id: number;
    liberado: boolean;
    nome: string;
  }[];
}

interface IGetThemeContentParams {
  id: number;
  id_area?: number;
}
interface IGetThemeContentResponse {
  e_favorito: boolean;
  e_liberado: boolean;
  lista: {
    conteudo: string;
    id: number;
    nome: string;
  }[];
  nome: string;
}

const pediatricsCalculators = [
  {
    id: 99999999999999,
    uniqueId: 99999999999999,
    name: 'Dose emergência pediatria',
    sku: 'dose_emergencia',
    isFavorite: false,
    isReleased: false,
  },
  {
    id: 99999999999998,
    uniqueId: 99999999999998,
    name: 'Dose sintomáticos pediatria',
    sku: 'dose_sistomatico',
    isFavorite: false,
    isReleased: true,
  },
  {
    id: 99999999999997,
    uniqueId: 99999999999997,
    name: 'Dose antibióticos pediatria',
    sku: 'dose_antibiotico',
    isFavorite: false,
    isReleased: false,
  },
  {
    id: 99999999999996,
    uniqueId: 99999999999996,
    name: 'Hidratação pediatria (Holliday)',
    sku: 'hidratacao_pediatria',
    isFavorite: false,
    isReleased: false,
  },
];

class MajorAreaService implements IMajorAreasRepository {
  public async getAll(): Promise<IGetAllMajorAreasOutputData> {
    try {
      const response = await HttpClient.get<IGetAllResponse>({
        path: 'areas.php',
      });
      const output: IGetAllMajorAreasOutputData = {
        list: response.data.lista.map((item) => {
          const majorArea: MajorArea = {
            id: item.id,
            name: item.nome,
          };
          return majorArea;
        }),
      };
      return output;
    } catch (err) {
      if (err instanceof HttpClientError) {
        throw new HttpClientError({
          body: err.body,
          message: err.message,
          response: err.response,
        });
      }
      if (err instanceof Error) {
        throw new Error(err.message);
      }
      throw new Error(String(JSON.stringify(err)));
    }
  }

  public async getThemes(
    inputData: IGetAllMajorAreaThemesInputData,
  ): Promise<IGetAllMajorAreaThemesOutputData> {
    try {
      const params: IGetThemesParams = {
        id: inputData.id,
      };
      if (inputData.specialtyId) {
        params.especialidade = inputData.specialtyId;
      }
      if (inputData.sort) {
        params.ordenacao = inputData.sort;
      }
      const response = await HttpClient.get<
        IGetThemesResponse,
        IGetThemesParams
      >({
        path: 'temas.php',
        params,
      });
      const output: IGetAllMajorAreaThemesOutputData = {
        list: response.data.lista.map((item, index) => {
          const majorAreaTheme: MajorAreaTheme = {
            id: item.id,
            name: item.nome,
            isFavorite: item.e_favorito,
            uniqueId: index,
            isReleased: item.liberado,
          };
          return majorAreaTheme;
        }),
      };
      if (
        inputData.id === 4 &&
        !inputData.specialtyId &&
        inputData.sort === EMajorAreaThemeOrder.Default
      ) {
        return { list: [...pediatricsCalculators, ...output.list] };
      }
      return output;
    } catch (err) {
      if (err instanceof HttpClientError) {
        throw new HttpClientError({
          body: err.body,
          message: err.message,
          response: err.response,
        });
      }
      if (err instanceof Error) {
        throw new Error(err.message);
      }
      throw new Error(String(JSON.stringify(err)));
    }
  }

  public async getThemeContent(
    inputData: IGetThemeContentInputData,
  ): Promise<IGetThemeContentOutputData> {
    try {
      const response = await HttpClient.get<
        IGetThemeContentResponse,
        IGetThemeContentParams
      >({
        path: 'topicos.php',
        params: {
          id: inputData.id,
          id_area: inputData.areaId,
        },
      });
      const topicsSerialized: ITopic[] = [];
      let content = '';
      for (const topic of response.data.lista) {
        topicsSerialized.push({
          id: topic.id,
          name: topic.nome.trim(),
        });
        let topicContent = topic.conteudo;
        topicContent = topicContent.replaceAll('\\', '');
        topicContent = topicContent.replace(
          /padding-inline-start:(\s+)?48px(;)?/g,
          'padding-inline-start: 24px;',
        );
        const maxWidth = 768;
        topicContent = topicContent.replaceAll(
          '<img',
          `<img style="width: ${window.innerWidth > maxWidth ? maxWidth : window.innerWidth - 16}px;"`,
        );

        const regexGetTagA = /<a\b[^>]*>(.*?)<\/a>/;
        const regexLinkGPMED =
          /<a[^>]*href\s*=\s*['"](.*?(?:medicamento\.php|topico\.php|med\.php|calculadora_\w+\.php).*?)['"][^>]*>(.*?)<\/a>(<\/a>)?/;
        const regexLinkGPMED2 =
          /<a[^>]*href\s*=\s*['"](.*?(?:medicamentos|topicos|calculadoras).*?)['"][^>]*>(.*?)<\/a>/;
        let hasLink = true;
        while (hasLink) {
          const resultGetTagA = regexGetTagA.exec(topicContent);
          hasLink = !!resultGetTagA;
          if (!resultGetTagA) {
            break;
          }
          let resultRegexLinkGPMED = regexLinkGPMED.exec(topicContent);
          let resultRegexLinkGPMED2 = regexLinkGPMED2.exec(topicContent);
          if (resultRegexLinkGPMED) {
            const textMatch = resultRegexLinkGPMED[0];

            let html = textMatch;
            let hasMoreThanOneLink = false;
            const regexHasMoreThanOneLink =
              /<a[^>]*>.*?<a[^>]*>.*?<\/a>.*?<\/a>/;
            hasMoreThanOneLink = regexHasMoreThanOneLink.test(html);
            while (hasMoreThanOneLink) {
              html = html.replace(/<a[^>]*>(<a[^>]*>.*?<\/a>)<\/a>/, '$1');

              hasMoreThanOneLink = regexHasMoreThanOneLink.test(html);
            }
            resultRegexLinkGPMED = regexLinkGPMED.exec(html) as RegExpExecArray;

            const href = resultRegexLinkGPMED[1];
            const name = resultRegexLinkGPMED[2];
            const drugToAppend = formatterDrugHtml(href, name);

            if (drugToAppend) {
              topicContent = topicContent.replaceAll(textMatch, drugToAppend);
            }

            const themeToAppend = formatterThemeHtml(href, name);
            if (themeToAppend) {
              topicContent = topicContent.replace(
                regexLinkGPMED,
                themeToAppend,
              );
            }

            const calculatorToAppend = formatterCalculatorHtml(href, name);
            if (calculatorToAppend) {
              topicContent = topicContent.replaceAll(
                textMatch,
                calculatorToAppend,
              );
            }
          } else if (resultRegexLinkGPMED2) {
            const textMatch = resultRegexLinkGPMED2[0];

            const html = textMatch;
            resultRegexLinkGPMED2 = regexLinkGPMED2.exec(
              html,
            ) as RegExpExecArray;

            const href = resultRegexLinkGPMED2[1];
            const name = resultRegexLinkGPMED2[2];
            const drugToAppend = formatterDrugHtml2(href, name);

            if (drugToAppend) {
              topicContent = topicContent.replaceAll(textMatch, drugToAppend);
            }

            const themeToAppend = formatterThemeHtml2(href, name);
            if (themeToAppend) {
              topicContent = topicContent.replace(
                regexLinkGPMED2,
                themeToAppend,
              );
            }

            const calculatorToAppend = formatterCalculatorHtml2(href, name);
            if (calculatorToAppend) {
              topicContent = topicContent.replaceAll(
                textMatch,
                calculatorToAppend,
              );
            }
          } else {
            let resultRegexExternalLink = regexGetTagA.exec(
              topicContent,
            ) as RegExpExecArray;

            const textMatch = resultRegexExternalLink[0];

            let html = textMatch;
            let hasMoreThanOneLink = false;
            const regexHasMoreThanOneLink =
              /<a[^>]*>.*?<a[^>]*>.*?<\/a>.*?<\/a>/;
            hasMoreThanOneLink = regexHasMoreThanOneLink.test(html);
            while (hasMoreThanOneLink) {
              html = html.replace(/<a[^>]*>(<a[^>]*>.*?<\/a>)<\/a>/, '$1');
              hasMoreThanOneLink = regexHasMoreThanOneLink.test(html);
            }
            resultRegexExternalLink =
              /<a\b[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/.exec(
                html,
              ) as RegExpExecArray;

            const href = resultRegexExternalLink[1];
            const name = resultRegexExternalLink[2];
            const externalLinkToAppend = formatterExternalLinkHtml(href, name);
            if (externalLinkToAppend) {
              topicContent = topicContent.replaceAll(
                textMatch,
                externalLinkToAppend,
              );
            }
          }
        }
        content = `${content}<h1 id="${topic.id}_${topic.nome.trim()}" style="color: #211F54; font-weight: bold; font-size:24px;">${topic.nome}</h1>${topicContent}<br />`;
      }
      const output: IGetThemeContentOutputData = {
        isFavorite: response.data.e_favorito,
        title: response.data.nome,
        topics: topicsSerialized,
        content,
        isReleased: response.data.e_liberado,
      };
      return output;
    } catch (err) {
      if (err instanceof HttpClientError) {
        throw new HttpClientError({
          body: err.body,
          message: err.message,
          response: err.response,
        });
      }
      if (err instanceof Error) {
        throw new Error(err.message);
      }
      throw new Error(String(JSON.stringify(err)));
    }
  }
}

export default new MajorAreaService();
